import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, MoveHorizontal, Loader2 } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import { getCleanOptions, getCurrentISOWeek, getCurrentISOYear, getISOWeekDateRange, getISOWeeksInYear, formatDateRange, pickBestCombination, DEFAULT_COMBINATION_PREFERENCE, DEFAULT_RANK_FIRST } from '../../utils/analyticsHelpers';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { usePriceClassifierClassify } from '../../../utils/api-hooks/analytics';
import ChartHelpButton from '../ChartHelpButton';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, PRESET_FILTER_ROW_Y, PRESET_FILTER_OPEN_PT } from '../analytics/PresetChartChrome';

// ─── Tier Configuration ────────────────────────────────────────────────────────

const TIERS = [
  { key: 'historic_low', primary: '#f9a8a8', bg: '#fef5f5', border: '#f9c9c9', text: '#b45050', darkBg: '#3a2020', darkBorder: '#5a3030', darkText: '#f9a8a8' },
  { key: 'low',          primary: '#f07272', bg: '#fef0f0', border: '#f5a3a3', text: '#c0392b', darkBg: '#3a1f1f', darkBorder: '#5a2f2f', darkText: '#f07272' },
  { key: 'balanced',     primary: '#e74c3c', bg: '#fdeceb', border: '#f1807a', text: '#c0392b', darkBg: '#3a1c1a', darkBorder: '#5a2c2a', darkText: '#e74c3c' },
  { key: 'high',         primary: '#c0392b', bg: '#fce8e6', border: '#e06050', text: '#962d22', darkBg: '#3a1818', darkBorder: '#5a2828', darkText: '#c0392b' },
  { key: 'historic_high',primary: '#7b241c', bg: '#f9e2e0', border: '#c0534a', text: '#5b1a14', darkBg: '#2a1010', darkBorder: '#4a2020', darkText: '#c0534a' },
];

const GAUGE_FLEX = [0.7, 1.0, 1.2, 1.0, 0.7];

const getTierColor = (tierIndex, prop, isDark) => {
  const tier = TIERS[tierIndex] || TIERS[2];
  if (isDark) {
    if (prop === 'bg') return tier.darkBg;
    if (prop === 'border') return tier.darkBorder;
    if (prop === 'text') return tier.darkText;
  }
  return tier[prop] || tier.primary;
};

// ─── Classification Utilities ──────────────────────────────────────────────────

const getClassification = (price, classifications) => {
  if (!classifications) return undefined;
  const key = price.toFixed(2);
  return classifications[key];
};

const classifyFromThresholds = (price, thresholds) => {
  if (!thresholds || thresholds.length < 4) return 2;
  if (price < thresholds[0]) return 0;
  if (price < thresholds[1]) return 1;
  if (price < thresholds[2]) return 2;
  if (price < thresholds[3]) return 3;
  return 4;
};

// Map price to visual gauge position using thresholds + flex ratios
// so the pointer always appears in the correct visual tier segment
const getGaugePercent = (price, thresholds) => {
  if (!thresholds || thresholds.length < 4) return 50;

  const totalFlex = GAUGE_FLEX.reduce((a, b) => a + b, 0);
  // Cumulative start % for each tier segment
  const segStarts = [];
  let cum = 0;
  for (let i = 0; i < GAUGE_FLEX.length; i++) {
    segStarts.push(cum);
    cum += (GAUGE_FLEX[i] / totalFlex) * 100;
  }

  // Tier boundaries in price space: [-Infinity, t0, t1, t2, t3, +Infinity]
  const bounds = [-Infinity, ...thresholds, Infinity];

  // Find which tier the price falls in
  let tierIdx = 0;
  for (let i = 0; i < 4; i++) {
    if (price >= thresholds[i]) tierIdx = i + 1;
  }

  const segStart = segStarts[tierIdx];
  const segWidth = (GAUGE_FLEX[tierIdx] / totalFlex) * 100;
  const lo = bounds[tierIdx] === -Infinity ? thresholds[0] - (thresholds[1] - thresholds[0]) : bounds[tierIdx];
  const hi = bounds[tierIdx + 1] === Infinity ? thresholds[3] + (thresholds[3] - thresholds[2]) : bounds[tierIdx + 1];

  // Position within the segment (clamped 10%-90% to avoid edge overlap)
  const frac = hi === lo ? 0.5 : Math.max(0.1, Math.min(0.9, (price - lo) / (hi - lo)));
  return Math.max(1, Math.min(99, segStart + frac * segWidth));
};

// ─── Filter Configuration ──────────────────────────────────────────────────────

const FILTER_ORDER = ['type', 'variety', 'item_size', 'grade', 'package', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties'];

const FILTER_FIELDS = {
  type: 'type',
  variety: 'variety',
  item_size: 'item_size',
  grade: 'grade',
  package: 'package',
  storage: 'storage',
  condition: 'condition',
  environment: 'environment',
  quality: 'quality',
  appearance: 'appearance',
  properties: 'properties',
};

/** {@link FILTER_ORDER} as the [resultKey, rowProperty] pairs the scorer takes. */
const CLASSIFIER_COMBINATION_FIELDS = FILTER_ORDER.map((key) => [key, FILTER_FIELDS[key]]);

/**
 * One bucket per ISO week of history behind a combination, so the default lands
 * on the SKU with the most weeks to classify against rather than the one with
 * the most rows in a single week.
 *
 * Rows without week fields all fall into one bucket, which makes the scorer
 * degrade to ranking by row count rather than discarding the data outright.
 */
const classifierWeekBucket = (row) => {
  const year = row?.iso_year || row?.year;
  const week = row?.iso_week;
  return year && week ? `${year}-${week}` : 'all';
};

const isSelectableClassifierWeek = (week, year) =>
  Number.isFinite(week) && week >= 1 && week <= getISOWeeksInYear(year);

const getDefaultClassifierWeek = (year) => {
  const currentYear = getCurrentISOYear();
  const currentWeek = getCurrentISOWeek();
  return year === currentYear ? currentWeek : 1;
};

// ─── Gauge Bar Component ───────────────────────────────────────────────────────

const GaugeBar = ({ tierIndex, percent, height = 8, pointerSize = 14, isDark, showLabels = false, t, minLabel, maxLabel, fontSize: fs = 12 }) => (
  <div className="w-full">
    <div className="relative flex" style={{ height }}>
      {TIERS.map((tier, i) => (
        <div
          key={i}
          style={{
            flex: GAUGE_FLEX[i],
            backgroundColor: tier.primary,
            opacity: i === tierIndex ? 1 : 0.3,
            borderRadius: i === 0 ? `${height / 2}px 0 0 ${height / 2}px` : i === 4 ? `0 ${height / 2}px ${height / 2}px 0` : 0,
          }}
        />
      ))}
      {/* Pointer */}
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{
          left: `${percent}%`,
          marginLeft: -pointerSize / 2,
          width: pointerSize,
          height: pointerSize,
          borderRadius: '50%',
          backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          border: `3px solid ${TIERS[tierIndex]?.primary || '#e74c3c'}`,
          transition: 'left 0.07s linear',
          zIndex: 2,
        }}
      />
    </div>
    {showLabels && (
      <div className="flex justify-between mt-1">
        {minLabel && <span className="font-semibold" style={{ fontSize: fs - 3, color: isDark ? '#999' : '#6b6b6b' }}>{minLabel}</span>}
        {maxLabel && <span className="font-semibold" style={{ fontSize: fs - 3, color: isDark ? '#999' : '#6b6b6b' }}>{maxLabel}</span>}
      </div>
    )}
    {showLabels && (
      <div className="flex mt-0.5">
        {TIERS.map((tier, i) => (
          <div key={i} style={{ flex: GAUGE_FLEX[i], textAlign: i === 0 ? 'left' : i === 4 ? 'right' : 'center' }}>
            <span className="font-bold tracking-wider" style={{ fontSize: fs - 4, color: isDark ? '#888' : '#888' }}>
              {t(`charts.smartPriceClassifier.tier.${tier.key}`)}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ─── Classification Tag ────────────────────────────────────────────────────────

const ClassificationTag = ({ tierIndex, isDark, t, animate = false, fontSize: fs = 12 }) => {
  const tier = TIERS[tierIndex] || TIERS[2];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold tracking-wide whitespace-nowrap"
      style={{
        fontSize: fs - 2,
        backgroundColor: isDark ? tier.darkBg : tier.bg,
        border: `1px solid ${isDark ? tier.darkBorder : tier.border}`,
        color: isDark ? tier.darkText : tier.text,
        transition: 'all 0.2s ease',
        animation: animate ? 'tagPop 0.32s ease' : 'none',
      }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tier.primary }} />
      {t(`charts.smartPriceClassifier.tier.${tier.key}`)}
    </span>
  );
};

// ═════════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════════

const PriceClassifierChart = ({
  data = [],
  email,
  commodity,
  forceFiltersOpen = false,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
}) => {
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('price-classifier');
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const { fontSize } = useChartFontSize();

  // ─── Container sizing ────────────────────────────────────────────────────
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 100 });

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ w: width, h: height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Vertical scale: only affects padding/margins/spacing, NOT font sizes
  const vScale = Math.max(0.3, Math.min(1, containerSize.h / 700));
  const isCompact = containerSize.h < 450;
  const isTiny = containerSize.h < 300;

  // ─── Filter State ──────────────────────────────────────────────────────────
  // Widget dashboard opens expanded, as before. The preset view starts collapsed
  // so small screens stay behind the Filters toggle; from lg up the strip is
  // shown regardless via the CSS gate below.
  const [filtersExpanded, setFiltersExpanded] = useState(!forceFiltersOpen);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [modalOpen, setModalOpen] = useState({});
  const initializedRef = useRef(false);

  // Reset init guard when commodity changes so new commodity gets valid defaults if no snapshot exists.
  useLayoutEffect(() => {
    initializedRef.current = false;
  }, [commodity]);

  // ─── Price / Classification State ──────────────────────────────────────────
  const [inputValue, setInputValue] = useState('');
  const [basePrice, setBasePrice] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isClassified, setIsClassified] = useState(false);
  const [products, setProducts] = useState([]);
  const [weekInfo, setWeekInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [animatingTags, setAnimatingTags] = useState({});
  const prevClassifications = useRef({});
  const ITEMS_PER_PAGE = 10;

  // Build classify hook params
  const classifyFilters = useMemo(() => {
    const f = {};
    FILTER_ORDER.forEach(key => {
      f[key] = filterValues[key] || null;
    });
    return f;
  }, [filterValues]);

  const { classifyResult, isClassifying, classify } = usePriceClassifierClassify(
    email, commodity, selectedWeek, selectedYear, classifyFilters, basePrice
  );

  // ─── Derived: available years (data years + current/next ISO year for future weeks) ─
  const availableYears = useMemo(() => {
    if (!data || data.length === 0) return [];
    const currentYear = getCurrentISOYear();
    const years = new Set(data.map((d) => d.iso_year || d.year).filter(Boolean));
    years.add(currentYear);
    years.add(currentYear + 1);
    return [...years].sort((a, b) => b - a);
  }, [data]);

  // ─── Derived: available weeks for selected year (full ISO year, incl. future) ─
  const availableWeeks = useMemo(() => {
    if (!selectedYear) return [];
    return Array.from({ length: getISOWeeksInYear(selectedYear) }, (_, i) => i + 1);
  }, [selectedYear]);

  // ─── Cascade calculation ───────────────────────────────────────────────────
  // Values come from scoring whole combinations by the weeks of history behind
  // them, not from taking the most common value one field at a time. Greedy
  // chaining optimises each field against the slice the previous one left, and
  // it never considered leaving a mostly-blank field unset — so a rare concrete
  // value would discard the bulk of the rows before the next field was picked.
  const calculateCascade = useCallback((startData, overrides = {}) => {
    let filtered = startData;

    // Honour caller-pinned values, but only where they have rows: pinning a
    // value the commodity does not carry empties the slice and blanks every
    // remaining field. An override of '' means "unset", so it filters nothing.
    for (const key of FILTER_ORDER) {
      const override = overrides[key];
      if (!override) continue;
      const next = filtered.filter(item => item[FILTER_FIELDS[key]] === override);
      if (next.length > 0) filtered = next;
    }

    const result = pickBestCombination(filtered, {
      fields: CLASSIFIER_COMBINATION_FIELDS,
      preferred: DEFAULT_COMBINATION_PREFERENCE,
      rankFirst: DEFAULT_RANK_FIRST,
      bucketOf: classifierWeekBucket,
      commodity,
    });

    // A pinned value always wins over the scored one.
    for (const key of FILTER_ORDER) {
      if (overrides[key] !== undefined) result[key] = overrides[key];
    }

    return result;
  }, []);

  // ─── Available options per filter (for modals) ─────────────────────────────
  const availableOptions = useMemo(() => {
    if (!data || data.length === 0) return {};
    let filtered = data;
    if (selectedYear) filtered = filtered.filter(d => (d.iso_year || d.year) === selectedYear);

    const opts = {};
    for (const key of FILTER_ORDER) {
      const field = FILTER_FIELDS[key];
      opts[key] = getCleanOptions(filtered, field, commodity);
      if (filterValues[key]) {
        filtered = filtered.filter(item => item[field] === filterValues[key]);
      }
    }
    return opts;
  }, [data, selectedYear, filterValues, commodity]);

  // Do NOT clear initializedRef in useEffect([data]): effects run after useLayoutEffect, so that
  // would run after Firestore hydrate (usePersistWidgetSnapshot) and let the init effect below
  // overwrite saved year/week/filters — then persist saves defaults back to Firebase.

  const hydrateClassifier = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (s.activeTab === 'all' || (s.activeTab !== undefined && /^[0-4]$/.test(String(s.activeTab)))) {
        setActiveTab(s.activeTab === 'all' ? 'all' : String(s.activeTab));
      }
      if (typeof s.currentPage === 'number' && s.currentPage >= 1) setCurrentPage(Math.floor(s.currentPage));

      const y =
        typeof s.selectedYear === 'number' && availableYears.includes(s.selectedYear)
          ? s.selectedYear
          : availableYears[0];
      setSelectedYear(y);

      const yearData = data.filter((d) => (d.iso_year || d.year) === y);
      const w =
        typeof s.selectedWeek === 'number' && isSelectableClassifierWeek(s.selectedWeek, y)
          ? s.selectedWeek
          : getDefaultClassifierWeek(y);
      setSelectedWeek(w);

      if (yearData.length === 0 && s.filterValues && typeof s.filterValues === 'object') {
        setFilterValues(s.filterValues);
      } else {
        // Every load opens on the combination with the most weeks of history,
        // ignoring the selection saved from the last visit — restoring it
        // brought the user back to whatever thin slice they last drilled into.
        setFilterValues(pickBestCombination(yearData, {
          fields: CLASSIFIER_COMBINATION_FIELDS,
          preferred: DEFAULT_COMBINATION_PREFERENCE,
      rankFirst: DEFAULT_RANK_FIRST,
          bucketOf: classifierWeekBucket,
          commodity,
        }));
      }
    },
    [data, availableYears, commodity]
  );

  const snapshotClassifier = useCallback(
    () => ({
      filtersExpanded,
      selectedYear,
      selectedWeek,
      filterValues,
      activeTab,
      currentPage,
    }),
    [filtersExpanded, selectedYear, selectedWeek, filterValues, activeTab, currentPage]
  );

  usePersistWidgetSnapshot(
    'price-classifier',
    commodity,
    !!(data && data.length > 0 && availableYears.length > 0),
    hydrateClassifier,
    snapshotClassifier,
    [filtersExpanded, selectedYear, selectedWeek, filterValues, activeTab, currentPage]
  );

  // Defaults only when there is no Firestore snapshot; after analytics state is fetched.
  useEffect(() => {
    if (!analyticsPrefsReady || !data || data.length === 0 || initializedRef.current) return;
    initializedRef.current = true;

    const currentWeek = getCurrentISOWeek();
    const currentYear = getCurrentISOYear();
    const defaultYear = availableYears.includes(currentYear) ? currentYear : availableYears[0];
    setSelectedYear(defaultYear);

    const yearData = data.filter(d => (d.iso_year || d.year) === defaultYear);
    const defaultWeek = defaultYear === currentYear
      ? (currentWeek - 1 >= 1 ? currentWeek - 1 : currentWeek)
      : 1;
    setSelectedWeek(defaultWeek);

    const cascade = calculateCascade(yearData);
    setFilterValues(cascade);
  }, [analyticsPrefsReady, data, availableYears, calculateCascade]);

  // ─── Handle classify result ────────────────────────────────────────────────
  useEffect(() => {
    if (classifyResult && classifyResult.products) {
      setProducts(classifyResult.products);
      setWeekInfo(classifyResult.week || null);
      setIsClassified(true);
      setActiveTab('all');
      setCurrentPage(1);
    }
  }, [classifyResult]);

  // ─── Year change handler ───────────────────────────────────────────────────
  const handleYearChange = useCallback((newYear) => {
    const yearNum = Number(newYear);
    setSelectedYear(yearNum);

    const yearData = data.filter(d => (d.iso_year || d.year) === yearNum);
    setSelectedWeek(getDefaultClassifierWeek(yearNum));

    if (yearData.length > 0) {
      const cascade = calculateCascade(yearData);
      setFilterValues(cascade);
    }
    setIsClassified(false);
    setProducts([]);
  }, [data, calculateCascade]);

  // ─── Generic filter change handler ─────────────────────────────────────────
  const handleFilterChange = useCallback((level, newValue) => {
    let filtered = data;
    if (selectedYear) filtered = filtered.filter(d => (d.iso_year || d.year) === selectedYear);

    const levelIdx = FILTER_ORDER.indexOf(level);

    // Apply all filters up to this level
    for (let i = 0; i < levelIdx; i++) {
      const k = FILTER_ORDER[i];
      if (filterValues[k]) {
        filtered = filtered.filter(item => item[FILTER_FIELDS[k]] === filterValues[k]);
      }
    }

    // Apply the new value at this level
    const newVals = { ...filterValues, [level]: newValue || '' };
    if (newValue) filtered = filtered.filter(item => item[FILTER_FIELDS[level]] === newValue);

    // Cascade downstream: score the remaining fields as a whole combination, so
    // the classifier lands on the SKU with the most weeks of history inside the
    // value just picked, rather than the most common value one field at a time.
    const best = pickBestCombination(filtered, {
      fields: CLASSIFIER_COMBINATION_FIELDS,
      preferred: DEFAULT_COMBINATION_PREFERENCE,
      rankFirst: DEFAULT_RANK_FIRST,
      bucketOf: classifierWeekBucket,
      commodity,
    });
    for (let i = levelIdx + 1; i < FILTER_ORDER.length; i++) {
      const k = FILTER_ORDER[i];
      newVals[k] = best[k] || '';
    }

    setFilterValues(newVals);
    setIsClassified(false);
    setProducts([]);
  }, [data, selectedYear, filterValues, commodity]);

  // ─── Week change handler ───────────────────────────────────────────────────
  const handleWeekChange = useCallback((newWeek) => {
    setSelectedWeek(Number(newWeek));
    setIsClassified(false);
    setProducts([]);
  }, []);

  // ─── Classify handler ─────────────────────────────────────────────────────
  const handleClassify = useCallback(() => {
    const price = parseFloat(inputValue);
    if (isNaN(price) || price <= 0) return;
    setBasePrice(price);
    setCurrentPrice(price);
    setTimeout(() => classify(), 0);
  }, [inputValue, classify]);

  // ─── Slider handler ───────────────────────────────────────────────────────
  const handleSliderChange = useCallback((e) => {
    const newPrice = parseFloat(e.target.value);
    setCurrentPrice(newPrice);
    setInputValue(newPrice.toFixed(2));
  }, []);

  // ─── Detect tier changes for animations ────────────────────────────────────
  useEffect(() => {
    if (!isClassified || !products.length || currentPrice === null) return;

    const newAnimating = {};
    products.forEach((product, idx) => {
      const newTier = getClassification(currentPrice, product.classifications) ??
        classifyFromThresholds(currentPrice, product.thresholds);
      const prevTier = prevClassifications.current[idx];
      if (prevTier !== undefined && prevTier !== newTier) {
        newAnimating[idx] = true;
      }
      prevClassifications.current[idx] = newTier;
    });

    if (Object.keys(newAnimating).length > 0) {
      setAnimatingTags(newAnimating);
      const timer = setTimeout(() => setAnimatingTags({}), 350);
      return () => clearTimeout(timer);
    }
  }, [currentPrice, isClassified, products]);

  // ─── Derived: classifications for current price ────────────────────────────
  const productClassifications = useMemo(() => {
    if (!isClassified || !products.length || currentPrice === null) return [];
    return products.map(product => {
      const tier = getClassification(currentPrice, product.classifications) ??
        classifyFromThresholds(currentPrice, product.thresholds);
      const percent = getGaugePercent(currentPrice, product.thresholds);
      return { ...product, tierIndex: tier, gaugePercent: percent };
    });
  }, [isClassified, products, currentPrice]);

  // ─── Derived: tab counts ───────────────────────────────────────────────────
  const tabCounts = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    productClassifications.forEach(p => { counts[p.tierIndex] = (counts[p.tierIndex] || 0) + 1; });
    return counts;
  }, [productClassifications]);

  // ─── Derived: filtered products by active tab ──────────────────────────────
  const filteredProducts = useMemo(() => {
    if (activeTab === 'all') return productClassifications;
    const tierIdx = parseInt(activeTab, 10);
    return productClassifications.filter(p => p.tierIndex === tierIdx);
  }, [productClassifications, activeTab]);

  const isSingleProduct = productClassifications.length === 1;
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // ─── Slider range ──────────────────────────────────────────────────────────
  const sliderMin = basePrice !== null ? Math.max(0, basePrice - 10) : 0;
  const sliderMax = basePrice !== null ? basePrice + 10 : 20;

  // ─── Week banner text ─────────────────────────────────────────────────────
  const weekBannerText = useMemo(() => {
    if (!selectedWeek || !selectedYear) return '';
    const range = getISOWeekDateRange(selectedWeek, selectedYear);
    if (!range) return `WEEK ${selectedWeek}`;
    return t('charts.smartPriceClassifier.week.banner', {
      n: selectedWeek,
      start_date: range.start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }),
      end_date: range.end.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    });
  }, [selectedWeek, selectedYear, t]);

  // ─── Week dropdown formatter ───────────────────────────────────────────────
  const formatWeekOption = useCallback((week) => {
    const range = getISOWeekDateRange(week, selectedYear);
    if (!range) return `Week ${week}`;
    const start = range.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = range.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Week ${week} · ${start} – ${end}`;
  }, [selectedYear]);

  // ─── Hint text ─────────────────────────────────────────────────────────────
  const hintText = useMemo(() => {
    if (!filterValues.type || filterValues.type === 'All Types') {
      return t('charts.smartPriceClassifier.hint.classifying_across', { types: t('charts.smartPriceClassifier.filter.all_types'), n: selectedWeek });
    }
    const parts = [filterValues.type, filterValues.variety, filterValues.item_size].filter(Boolean).join(' ');
    return t('charts.smartPriceClassifier.hint.classifying_for', { product: parts, n: selectedWeek });
  }, [filterValues, selectedWeek, t]);

  // ─── Render ────────────────────────────────────────────────────────────────
  // v() scales only vertical spacing (padding, margins) based on container height
  const v = (px) => Math.round(px * vScale);

  return (
    <div
      ref={containerRef}
      className={forceFiltersOpen ? 'w-full h-full min-h-0 flex flex-col px-4 pt-4' : 'h-full w-full overflow-y-auto px-4'}
      style={{ minHeight: 34 }}
    >
      {/* CSS for tag pop animation */}
      <style>{`@keyframes tagPop { 0% { transform: scale(1); } 50% { transform: scale(1.15); } 100% { transform: scale(1); } }`}</style>

      {/* ── Title ──────────────────────────────────────────────────────────── */}
      <PresetChartTitle
        forceFiltersOpen={forceFiltersOpen}
        title={t('charts.smartPriceClassifier.title')}
        theme={theme}
        fontSize={fontSize}
        titleAfter={
          <ChartHelpButton
            title={t('charts.smartPriceClassifier.title')}
            description={t('charts.smartPriceClassifier.description')}
          />
        }
        right={
          <ChartFullscreenTitleControl
            analyticsFullscreen={analyticsFullscreen}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
          />
        }
      />

      <div
        className={
          forceFiltersOpen && !presetSingleChartFullscreen
            ? 'flex-1 min-h-0 overflow-x-hidden overflow-y-auto'
            : 'contents'
        }
      >
      {/* ── Filters Panel ─────────────────────────────────────────────────── */}
      <div className={`border-b ${isDark ? 'border-[#3a3c3b]' : 'border-[#e8e5e0]'}`}>
        {/* Collapsible in the widget dashboard, and below lg in the preset view
            too; lg:hidden once forceFiltersOpen since desktop preset keeps the
            panel open via the CSS gate below. */}
        <button
          type="button"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className={`w-full flex items-center justify-between py-2 text-sm ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-[#6b6b6b] hover:bg-[#fafaf8]'} ${forceFiltersOpen ? 'px-4 lg:hidden' : ''}`}
          style={{fontSize: 14 }}
        >
          {t('charts.smartPriceClassifier.filter.label')}
          {filtersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {(forceFiltersOpen || filtersExpanded) && (
          <div
            className={`${isTiny ? 'overflow-y-auto' : ''}${
              forceFiltersOpen
                ? ` ${PRESET_FILTER_ROW_Y}${filtersExpanded ? ` ${PRESET_FILTER_OPEN_PT}` : ' max-lg:hidden'}`
                : ''
            }`}
            style={{
              // Preset view: vertical padding comes from the shared classes and
              // the horizontal inset from the outer px-4, so the chips align
              // with the title. Widget view keeps its inline padding.
              ...(forceFiltersOpen ? {} : { padding: `0px 16px ${v(12)}px` }),
              ...(isTiny ? { maxHeight: 180 } : {}),
            }}
          >
            {forceFiltersOpen ? (
              <PresetFilterStrip
                forceFiltersOpen
                presetSingleChartFullscreen={presetSingleChartFullscreen}
                analyticsFullscreen={analyticsFullscreen}
                titleHasFullscreenControl
              >
                <FilterDropdown
                  label={t('charts.smartPriceClassifier.filter.year')}
                  value={selectedYear || ''}
                  onClick={() => setModalOpen({ year: true })}
                  compact
                />
                {FILTER_ORDER.map((key) => {
                  const opts = availableOptions[key] || [];
                  if (opts.length === 0) return null;
                  return (
                    <FilterDropdown
                      key={key}
                      label={t(`charts.smartPriceClassifier.filter.${key}`)}
                      value={filterValues[key] || ''}
                      onClick={() => setModalOpen({ [key]: true })}
                      compact
                    />
                  );
                })}
                <FilterDropdown
                  label={t('charts.smartPriceClassifier.filter.week')}
                  value={selectedWeek ? formatWeekOption(selectedWeek) : ''}
                  onClick={() => setModalOpen({ week: true })}
                  compact
                />
              </PresetFilterStrip>
            ) : (
              <div
                className="grid gap-2"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}
              >
                {/* Year */}
                <FilterDropdown
                  label={t('charts.smartPriceClassifier.filter.year')}
                  value={selectedYear || ''}
                  onClick={() => setModalOpen({ year: true })}
                />

                {/* Dynamic filters */}
                {FILTER_ORDER.map(key => {
                  const opts = availableOptions[key] || [];
                  if (opts.length === 0) return null;
                  return (
                    <FilterDropdown
                      key={key}
                      label={t(`charts.smartPriceClassifier.filter.${key}`)}
                      value={filterValues[key] || ''}
                      onClick={() => setModalOpen({ [key]: true })}
                    />
                  );
                })}

                {/* Week (always last) */}
                <FilterDropdown
                  label={t('charts.smartPriceClassifier.filter.week')}
                  value={selectedWeek ? formatWeekOption(selectedWeek) : ''}
                  onClick={() => setModalOpen({ week: true })}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Week Banner ───────────────────────────────────────────────────── */}
      <div
        className="text-center text-[14px] font-semibold"
        style={{
          padding: `${v(8)}px 0`,
          backgroundColor: isDark ? '#222' : '#fafaf8',
          color: isDark ? '#e0e0e0' : '#1a1a1a',
          borderBottom: `1px solid ${isDark ? '#3a3c3b' : '#e8e5e0'}`,
        }}
      >
        {weekBannerText}
      </div>

      {/* ── Price Input + Classify ────────────────────────────────────────── */}
      <div className="py-2">
        {!isCompact && (
          <div className="text-[11px] font-bold tracking-wider" style={{ marginBottom: v(6), color: isDark ? '#999' : '#6b6b6b' }}>
            {t('charts.smartPriceClassifier.input.label')}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[20px] font-bold" style={{ color: isDark ? '#ccc' : '#1a1a1a' }}>$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleClassify(); }}
              className="pl-8 pr-3 w-[160px] text-[20px] font-bold rounded-lg outline-none"
              style={{
                padding: `${v(8)}px 10px ${v(8)}px 30px`,
                border: '2px solid #22c55e',
                backgroundColor: isDark ? '#282A29' : '#fff',
                color: isDark ? '#fff' : '#1a1a1a',
                colorScheme: isDark ? 'dark' : 'light',
              }}
              aria-label="Price input"
            />
          </div>
          <button
            onClick={handleClassify}
            disabled={isClassifying || !inputValue}
            className="flex items-center justify-center rounded-[10px] text-[14px] font-semibold text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: isDark ? '#3a3a3a' : '#1a1a1a', border: isDark ? '1px solid rgba(255,255,255,0.15)' : 'none', padding: `${v(10)}px 20px` }}
          >
            {isClassifying ? <Loader2 className="animate-spin" size={16} /> : t('charts.smartPriceClassifier.action.classify')}
          </button>
          <span className="text-[12px]" style={{ color: isDark ? '#888' : '#6b6b6b' }}>
            {hintText}
          </span>
        </div>
      </div>

      {/* ── Slider ────────────────────────────────────────────────────────── */}
      {isClassified && basePrice !== null && (
        <div
          className="rounded-[10px]"
          style={{
            margin: `0 16px ${v(8)}px`,
            padding: `${v(10)}px 12px`,
            backgroundColor: isDark ? '#222' : '#fafaf8',
            border: `1px solid ${isDark ? '#3a3c3b' : '#e8e5e0'}`,
          }}
        >
          {/* Slider header */}
          {!isCompact && (
            <div className="flex items-center justify-between" style={{ marginBottom: v(8) }}>
              <div className="flex items-center gap-2 text-[11px] font-bold tracking-wider" style={{ color: isDark ? '#999' : '#6b6b6b' }}>
                <MoveHorizontal size={13} />
                {t('charts.smartPriceClassifier.slider.title')}
              </div>
              <span className="text-[11px]" style={{ color: isDark ? '#888' : '#6b6b6b' }}>
                ${sliderMin.toFixed(2)} – ${sliderMax.toFixed(2)} (±$10)
              </span>
            </div>
          )}

          {/* Price bubble */}
          <div className="relative" style={{ marginBottom: v(2), height: 20 }}>
            <div
              className="absolute px-2 py-0.5 rounded text-[11px] font-bold text-white -translate-x-1/2"
              style={{
                bottom: 0,
                left: `${((currentPrice - sliderMin) / (sliderMax - sliderMin)) * 100}%`,
                backgroundColor: '#1a1a1a',
                fontFamily: 'monospace',
                transition: 'left 0.05s linear',
              }}
            >
              ${currentPrice?.toFixed(2)}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-[#1a1a1a]" />
            </div>
          </div>

          {/* Track behind slider */}
          <div className="relative">
            <div className="rounded-full" style={{ height: 6, backgroundColor: isDark ? '#444' : '#d4d4d4' }} />
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              step={0.10}
              value={currentPrice || basePrice}
              onChange={handleSliderChange}
              onPointerDownCapture={(e) => e.stopPropagation()}
              onMouseDownCapture={(e) => e.stopPropagation()}
              onTouchStartCapture={(e) => e.stopPropagation()}
              className="widget-no-drag absolute inset-0 w-full opacity-0 cursor-grab active:cursor-grabbing"
              style={{ height: 24, marginTop: -9 }}
              aria-label="Price range slider"
            />
            {/* Custom thumb overlay */}
            <div
              className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
              style={{
                left: `${((currentPrice - sliderMin) / (sliderMax - sliderMin)) * 100}%`,
                marginLeft: -12,
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
                border: '3px solid #1a1a1a',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                transition: 'left 0.05s linear',
              }}
            />
          </div>

          {/* Min / Max labels */}
          <div className="flex justify-between" style={{ marginTop: v(4) }}>
            <span className="font-semibold" style={{ fontSize: fontSize - 2, color: isDark ? '#777' : '#999' }}>${sliderMin.toFixed(2)}</span>
            <span className="font-semibold" style={{ fontSize: fontSize - 2, color: isDark ? '#777' : '#999' }}>${sliderMax.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* ── Results ───────────────────────────────────────────────────────── */}
      {isClassified && productClassifications.length > 0 && (
        <div style={{ padding: `0 16px ${forceFiltersOpen && !presetSingleChartFullscreen ? 0 : v(10)}px` }}>
          {isSingleProduct ? (
            /* ── Single Product View ──────────────────────────────────── */
            <SingleProductView
              product={productClassifications[0]}
              currentPrice={currentPrice}
              isDark={isDark}
              t={t}
              scale={vScale}
              fontSize={fontSize}
            />
          ) : (
            /* ── Multi Product View ───────────────────────────────────── */
            <>
              {/* Result header */}
              <div style={{ marginBottom: v(8) }}>
                <span className="text-[32px] font-bold tracking-tight" style={{ color: isDark ? '#fff' : '#1a1a1a', letterSpacing: '-1px' }}>
                  ${currentPrice?.toFixed(2)}
                </span>
                <span className="ml-3 text-[13px]" style={{ color: isDark ? '#888' : '#6b6b6b' }}>
                  {t('charts.smartPriceClassifier.result.across_types', {
                    n: productClassifications.length,
                    w: selectedWeek,
                    y: selectedYear,
                  })}
                </span>
              </div>

              {/* Classification tabs */}
              <div className="flex items-center gap-1 overflow-x-auto" style={{ marginBottom: v(8), paddingBottom: v(2), borderBottom: `1px solid ${isDark ? '#3a3c3b' : '#e8e5e0'}` }}>
                <TabButton
                  label={t('charts.smartPriceClassifier.tab.all')}
                  count={productClassifications.length}
                  isActive={activeTab === 'all'}
                  onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
                  color="#1a1a1a"
                  isDark={isDark}
                />
                {TIERS.map((tier, i) => (
                  <TabButton
                    key={i}
                    label={t(`charts.smartPriceClassifier.tier.${tier.key}`)}
                    count={tabCounts[i]}
                    isActive={activeTab === String(i)}
                    onClick={() => { setActiveTab(String(i)); setCurrentPage(1); }}
                    color={tier.primary}
                    textColor={tier.text}
                    isDark={isDark}
                  />
                ))}
              </div>

              {/* Table header */}
              <div className="grid grid-cols-[2fr_1fr_3fr] gap-2 px-2 font-bold tracking-wider" style={{ fontSize: fontSize - 2, paddingTop: v(4), paddingBottom: v(4), color: isDark ? '#666' : '#999' }}>
                <span>{t('charts.smartPriceClassifier.table.col.product_type')}</span>
                <span>{t('charts.smartPriceClassifier.table.col.classification')}</span>
                <span>{t('charts.smartPriceClassifier.table.col.price_position')}</span>
              </div>

              {/* Table rows */}
              {paginatedProducts.length === 0 ? (
                <div className="text-center" style={{ fontSize, padding: `${v(14)}px 0`, color: isDark ? '#888' : '#999' }}>
                  {t('charts.smartPriceClassifier.table.empty', { price: `$${currentPrice?.toFixed(2)}` })}
                </div>
              ) : (
                paginatedProducts.map((product, idx) => (
                  <div
                    key={product.product_id || idx}
                    className="grid grid-cols-[2fr_1fr_3fr] gap-2 items-center px-2 transition-colors"
                    style={{
                      paddingTop: v(6),
                      paddingBottom: v(6),
                      borderLeft: `3px solid ${TIERS[product.tierIndex]?.primary || '#e74c3c'}`,
                      borderBottom: `1px solid ${isDark ? '#3a3c3b' : '#e8e5e0'}`,
                      transition: 'border-color 0.25s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isDark ? '#252725' : '#fafaf8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    {/* Product name */}
                    <div>
                      <div className="font-semibold" style={{ fontSize: fontSize + 1, color: isDark ? '#fff' : '#1a1a1a' }}>
                        {product.product_name}
                      </div>
                      {product.product_detail && (
                        <div style={{ fontSize: fontSize - 2, color: isDark ? '#888' : '#999' }}>
                          {product.product_detail}
                        </div>
                      )}
                    </div>

                    {/* Classification tag */}
                    <ClassificationTag
                      tierIndex={product.tierIndex}
                      isDark={isDark}
                      t={t}
                      animate={!!animatingTags[idx]}
                      fontSize={fontSize}
                    />

                    {/* Gauge */}
                    <GaugeBar
                      tierIndex={product.tierIndex}
                      percent={product.gaugePercent}
                      isDark={isDark}
                      t={t}
                      fontSize={fontSize}
                      showLabels
                      minLabel={`$${product.price_min?.toFixed(2)}`}
                      maxLabel={`$${product.price_max?.toFixed(2)}`}
                    />
                  </div>
                ))
              )}

              {/* Pagination */}
              {filteredProducts.length > ITEMS_PER_PAGE && (
                <div className="flex items-center justify-between" style={{ marginTop: v(8) }}>
                  <span style={{ fontSize, color: isDark ? '#888' : '#999' }}>
                    {t('charts.smartPriceClassifier.pagination.showing', {
                      start: (currentPage - 1) * ITEMS_PER_PAGE + 1,
                      end: Math.min(currentPage * ITEMS_PER_PAGE, filteredProducts.length),
                      total: filteredProducts.length,
                    })}
                  </span>
                  <div className="flex gap-1">
                    <PaginationButton
                      label="‹"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(p => p - 1)}
                      isDark={isDark}
                    />
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
                      Math.max(0, currentPage - 3),
                      currentPage + 2
                    ).map(page => (
                      <PaginationButton
                        key={page}
                        label={String(page)}
                        isActive={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                        isDark={isDark}
                      />
                    ))}
                    <PaginationButton
                      label="›"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(p => p + 1)}
                      isDark={isDark}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      </div>


      {/* ── Filter Modals ─────────────────────────────────────────────────── */}
      <FilterModal
        isOpen={!!modalOpen.year}
        onClose={() => setModalOpen({})}
        title={t('charts.smartPriceClassifier.filter.year')}
        options={availableYears.map(String)}
        selectedValues={String(selectedYear)}
        onSelect={handleYearChange}
      />
      <FilterModal
        isOpen={!!modalOpen.week}
        onClose={() => setModalOpen({})}
        title={t('charts.smartPriceClassifier.filter.week')}
        options={availableWeeks.map(String)}
        selectedValues={String(selectedWeek)}
        onSelect={(v) => handleWeekChange(v)}
        displayFormatter={(w) => formatWeekOption(Number(w))}
      />
      {FILTER_ORDER.map(key => (
        <FilterModal
          key={key}
          isOpen={!!modalOpen[key]}
          onClose={() => setModalOpen({})}
          title={t(`charts.smartPriceClassifier.filter.${key}`)}
          options={availableOptions[key] || []}
          selectedValues={filterValues[key] || ''}
          onSelect={(v) => handleFilterChange(key, v)}
        />
      ))}
    </div>
  );
};

// ─── Single Product View ───────────────────────────────────────────────────────

const SingleProductView = ({ product, currentPrice, isDark, t, scale = 1, fontSize: fs = 12 }) => {
  const tier = TIERS[product.tierIndex] || TIERS[2];
  const v = (px) => Math.round(px * scale);
  const thresholds = product.thresholds || [];
  const balancedMin = thresholds[1];
  const balancedMax = thresholds[2];

  let distanceText = '';
  if (product.tierIndex < 2 && balancedMin !== undefined) {
    const diff = (balancedMin - currentPrice).toFixed(2);
    distanceText = t('charts.smartPriceClassifier.singleResult.desc_below_balanced', { diff: `$${diff}` });
  } else if (product.tierIndex === 2) {
    distanceText = t('charts.smartPriceClassifier.singleResult.desc_within_balanced');
  } else if (product.tierIndex > 2 && balancedMax !== undefined) {
    const diff = (currentPrice - balancedMax).toFixed(2);
    distanceText = t('charts.smartPriceClassifier.singleResult.desc_above_balanced', { diff: `$${diff}` });
  }

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{ border: `1px solid ${isDark ? tier.darkBorder : tier.border}` }}
    >
      {/* Colored header */}
      <div
        className="transition-colors duration-300"
        style={{ padding: `${v(14)}px 18px`, backgroundColor: isDark ? tier.darkBg : tier.bg }}
      >
        <div className="flex items-center gap-3" style={{ marginBottom: v(4) }}>
          <span className="font-bold" style={{ fontSize: fs + 16, color: isDark ? '#fff' : '#1a1a1a' }}>
            ${currentPrice?.toFixed(2)}
          </span>
          <ClassificationTag tierIndex={product.tierIndex} isDark={isDark} t={t} fontSize={fs} />
        </div>
        <div style={{ fontSize: fs, color: isDark ? '#999' : '#6b6b6b' }}>
          {product.product_name} · Week {product.week || ''}
        </div>
      </div>

      {/* Description + gauge */}
      <div style={{ padding: `${v(14)}px 18px`, backgroundColor: isDark ? '#1a1a1a' : '#ffffff' }}>
        <p className="leading-[1.6]" style={{ fontSize: fs + 1, marginBottom: v(12), color: isDark ? '#ccc' : '#333' }}>
          {t('charts.smartPriceClassifier.singleResult.desc_range', {
            min: `$${product.price_min?.toFixed(2)}`,
            max: `$${product.price_max?.toFixed(2)}`,
          })}{' '}
          {t('charts.smartPriceClassifier.singleResult.desc_classified_as', {
            price: `$${currentPrice?.toFixed(2)}`,
            tier: t(`charts.smartPriceClassifier.tier.${tier.key}`),
          })}{' '}
          {balancedMin !== undefined && balancedMax !== undefined && (
            <>
              {t('charts.smartPriceClassifier.singleResult.desc_balanced_range', {
                min: `$${balancedMin?.toFixed(2)}`,
                max: `$${balancedMax?.toFixed(2)}`,
              })},{' '}
              {distanceText}
            </>
          )}
        </p>

        {/* Flag tooltip + Gauge */}
        <div className="relative" style={{ paddingTop: 28, marginTop: v(6) }}>
          {/* Tooltip text */}
          <div
            className="absolute top-0 px-2 py-0.5 rounded font-bold text-white whitespace-nowrap transition-all duration-300"
            style={{
              fontSize: fs - 1,
              left: `${product.gaugePercent}%`,
              backgroundColor: tier.primary,
              transform: product.gaugePercent < 15 ? 'translateX(0)' : product.gaugePercent > 85 ? 'translateX(-100%)' : 'translateX(-50%)',
            }}
          >
            ${currentPrice?.toFixed(2)} is {t(`charts.smartPriceClassifier.tier.${tier.key}`).toLowerCase()}
          </div>
          {/* Arrow — always centered on gaugePercent to align with marker */}
          <div
            className="absolute w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent -translate-x-1/2"
            style={{
              top: 22,
              left: `${product.gaugePercent}%`,
              borderTopColor: tier.primary,
            }}
          />

          <GaugeBar
            tierIndex={product.tierIndex}
            percent={product.gaugePercent}
            height={12}
            pointerSize={18}
            isDark={isDark}
            t={t}
            fontSize={fs}
            showLabels
            minLabel={`$${product.price_min?.toFixed(2)}`}
            maxLabel={`$${product.price_max?.toFixed(2)}`}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Tab Button ────────────────────────────────────────────────────────────────

const TabButton = ({ label, count, isActive, onClick, color, textColor, isDark }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-colors"
    style={{
      color: isActive ? (textColor || color) : (isDark ? '#888' : '#999'),
      borderBottom: isActive ? `2px solid ${color}` : '2px solid transparent',
    }}
    role="tab"
    aria-selected={isActive}
  >
    {color !== '#1a1a1a' && (
      <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: color }} />
    )}
    {label}
    <span
      className="text-[10px] font-bold px-1 py-0.5 rounded"
      style={{
        fontFamily: 'monospace',
        backgroundColor: isActive ? (isDark ? '#333' : '#f0f0f0') : 'transparent',
        color: isActive ? (textColor || color) : (isDark ? '#666' : '#bbb'),
        transition: 'all 0.25s ease',
      }}
    >
      {count}
    </span>
  </button>
);

// ─── Pagination Button ─────────────────────────────────────────────────────────

const PaginationButton = ({ label, isActive, disabled, onClick, isDark }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="w-[30px] h-[30px] rounded-lg text-[12px] font-medium transition-colors disabled:opacity-40"
    style={{
      backgroundColor: isActive ? '#1a1a1a' : (isDark ? '#282A29' : '#f5f5f5'),
      color: isActive ? '#fff' : (isDark ? '#ccc' : '#333'),
    }}
  >
    {label}
  </button>
);

export default PriceClassifierChart;
